import {
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SecurityContext,
  SimpleChanges,
  inject,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ScreenSizes } from '../../classes/screen-sizes.class';
import { GROWTHBOOK } from '../../growthbook';
import { debounce } from '../../lib/debounce';
import { getHtmlText } from '../../lib/get-html-text';
import { getMentionHtml } from '../../lib/get-mention-html';
import {
  fullySelectSelectedElementOfType,
  getCurrentWord,
  getCurrentWordRange,
  getSelectedElementOfType,
  getSelectedRange,
  getSelectedText,
  getSelectedTextNodeMeta,
  getSelectedTextRect,
} from '../../lib/get-selected-text-rect';
import { sleep } from '../../lib/sleep';
import { unicodeToChar } from '../../lib/unicode-to-char';
import { AvatarService } from '../../services/avatar.service';
import { AutoCompleteItem } from '../../services/email-autocomplete.service';
import { ToastService } from '../../services/toast.service';
import { WysiwygService } from '../services/wysiwyg.service';

@Directive({
  selector: '[wysiwyg]',
  standalone: true,
})
export class WysiwygDirective implements OnChanges, OnInit {
  private elRef = inject<ElementRef<HTMLElement>>(ElementRef<HTMLElement>);
  private toasts = inject(ToastService);
  private _sanitizer = inject(DomSanitizer);
  private wysiwygService = inject(WysiwygService);
  private growthBook = inject(GROWTHBOOK);
  private avatarService = inject(AvatarService);

  @HostListener('keyup', ['$event'])
  onKeyDown(e: KeyboardEvent) {
    const mentionElement = getSelectedElementOfType(this.elRef.nativeElement, 'MENTION');
    if (mentionElement) {
      if (e.key === 'Backspace' || e.key === 'Delete') {
        this.deleteMention();
        e.preventDefault();
      } else {
        const selection = window.getSelection();
        const range = selection.getRangeAt(0);
        if (e.key === 'ArrowLeft') {
          // Move caret before the mention element
          range.setStartBefore(mentionElement);
          range.setEndBefore(mentionElement);
          selection.removeAllRanges();
          selection.addRange(range);
          e.preventDefault();
        } else if (e.key === 'ArrowRight') {
          // Move caret after the mention element
          range.setStartAfter(mentionElement);
          range.setEndAfter(mentionElement);
          selection.removeAllRanges();
          selection.addRange(range);
          selection.modify('move', 'right', 'character');
          e.preventDefault();
        }
      }
    }
  }

  @HostListener('keypress', ['$event'])
  onKeyPress(e: KeyboardEvent) {
    const value = this.getHtmlValue();
    const html = getHtmlText(value);
    const length = html.length;
    if (this.maxLength && length >= this.maxLength) {
      this.toasts.add('Max length exeeded', 'error');
      e.preventDefault();
      return false;
    }
    if (e.key === '@') {
      this.showMentionMenu();
    }

    if (e.key === 'Enter' && this.blockNewLines) {
      e.preventDefault();
      return false;
    }

    if (getSelectedElementOfType(this.elRef.nativeElement, 'MENTION')) {
      e.preventDefault();
      if (e.code === 'Space') {
        this.elRef.nativeElement.innerHTML += '&nbsp;';
        // move the caret to the end of the text node
        const range = document.createRange();
        range.selectNodeContents(this.elRef.nativeElement);
        range.collapse(false);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }

  @HostListener('input', ['$event'])
  async onInput() {
    if (this.mentionMode) {
      const currentWord = getCurrentWord(this.elRef.nativeElement);
      this.wysiwygService.updateMentionText(currentWord);
    }

    await sleep(0);
    this.save();
  }

  @HostListener('paste', ['$event'])
  async onPaste(e: ClipboardEvent) {
    const textToPaste = this.sanitize(e.clipboardData.getData('text'));
    const currentLength = getHtmlText(this.getHtmlValue()).length;
    const range = getSelectedRange(this.elRef.nativeElement);
    const selectedContent = range?.cloneContents()?.textContent;
    const selectedContentLength = selectedContent?.length || 0;

    e.preventDefault();
    if (this.maxLength && currentLength + textToPaste.length - selectedContentLength > this.maxLength) {
      this.toasts.add('Max length exeeded', 'error');
    }
    const charactersLeft = (this.maxLength || Infinity) - (currentLength - selectedContentLength);
    let newTextToPaste = textToPaste.slice(0, charactersLeft);

    if (this.allowLink) {
      newTextToPaste = newTextToPaste.replace(/((https?|ftp):\/\/[^\s/$.?#].[^\s]*)/g, '<a href="$1">$1</a>');
    }

    document.execCommand('insertHTML', false, newTextToPaste);

    this.save();
  }

  @HostBinding('class') elementClass: string;
  @Input('wysiwyg') model: string;
  @Input() allowHtml: boolean;
  @Input() allowLink = true;
  @Input() allowMentions: boolean;
  @Input() allowFontChanges: boolean;
  @Input() allowHugeFontSize: boolean;
  @Input() allowAlignment: boolean;
  // @Input('toolbar') toolbar: ToolbarComponent;
  @Input() maxLength?: number;
  @Input() fontFamily?: string;
  @Input() defaultFontFamily?: string;
  @Input() alignment?: string;
  @Input() fontSize?: 'small' | 'medium' | 'large' | 'huge';
  @Input() fontWeight?: string;
  @Input() textColor?: string;
  @Input() allowTextColorReset?: boolean;
  @Input() darkBackground?: boolean;
  @Input() wysiwygHoverIdentifier?: string;
  @Input() showContext?: boolean;
  @Input() blockNewLines?: boolean;
  @Output() wysiwygChange = new EventEmitter<string>();
  @Output() onFontChange = new EventEmitter();
  @Output() onFontSizeChange = new EventEmitter();
  @Output() onFontWeightChange = new EventEmitter();
  @Output() onAlignmentChange = new EventEmitter();
  @Output() onTextColorChange = new EventEmitter();
  @Output() onContextClicked = new EventEmitter();
  storedRange: Range;
  toolbarOpen = false;

  @HostListener('document:selectionchange', ['$event'])
  selectionChange() {
    this.setToolbar();
    this.setMentionMenu();
  }

  @HostListener('window:scroll', ['$event'])
  scroll() {
    this.setToolbar();
    this.setMentionMenu();
  }
  @HostListener('window:resize', ['$event'])
  resize() {
    this.setToolbar();
    this.setMentionMenu();
  }

  /**
   * By updating this property on keyup, and checking against it during
   * ngOnChanges, we can rule out change events fired by our own onKeyup.
   * Ideally we would not have to check against the whole string on every
   * change, could possibly store a flag during onKeyup and test against that
   * flag in ngOnChanges, but implementation details of Angular change detection
   * cycle might make this not work in some edge cases?
   */
  private lastViewModel: string;

  ngOnInit(): void {
    this.elementClass = 'wysiwyg-content no-style';
    if (!this.allowHtml) return;
    this.elementClass = 'wysiwyg-content';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['model'] && changes['model'].currentValue !== this.lastViewModel) {
      this.lastViewModel = this.model.trim();
      this.refreshView();
    }
  }

  getToolbarPosition(): DOMRect {
    const textRect = getSelectedTextRect(this.elRef.nativeElement);
    const bounds = this.elRef.nativeElement.getBoundingClientRect();
    if (!textRect) return;

    if (ScreenSizes.screenSize() === 'mobile') {
      return <DOMRect>{
        left: 0,
        width: window.innerWidth,
        top: Math.max(textRect.bottom, 60),
      };
    }

    return {
      ...textRect,
      top: Math.max(textRect.top, 120),
      left: bounds.left,
      width: bounds.width,
    };
  }

  mentionMode = false;

  setMentionMenu() {
    if (!this.mentionMode) return;
    // figuring out if the carrot is stil in the mention
    const currentWord = getCurrentWord(this.elRef.nativeElement);
    if (!currentWord || !currentWord.startsWith('@')) {
      this.mentionMode = false;
      this.wysiwygService.hideMentionMenu();
    }
  }

  showMentionMenu = debounce(() => {
    if (!this.allowMentions) return;
    const currentWord = getCurrentWord(this.elRef.nativeElement);
    if (!currentWord || !currentWord.startsWith('@')) {
      return;
    }
    this.mentionMode = true;
    this.wysiwygService.showMentionMenu({
      position: this.getToolbarPosition(),
      mentionText: currentWord,
      onClose: () => {
        this.mentionMode = false;
      },
      onMention: (user: AutoCompleteItem) => {
        this.addMention(user);
      },
      wysiwygHoverIdentifier: this.wysiwygHoverIdentifier,
    });
  }, 50);

  setToolbar() {
    if (!this.allowHtml && !this.allowFontChanges && !this.allowAlignment) return;
    if (!this.contenteditable) return;

    const text = getSelectedText(this.elRef.nativeElement);
    const aEl = getSelectedElementOfType(this.elRef.nativeElement, 'A');
    const mentionEl = getSelectedElementOfType(this.elRef.nativeElement, 'MENTION');
    const href = aEl ? aEl.getAttribute('href') : '';

    if ((!text && !href) || mentionEl) {
      if (!this.toolbarOpen) return;
      return this.wysiwygService.hideToolbar();
    }

    this.toolbarOpen = true;
    this.wysiwygService.showToolbar({
      allowFontChanges: this.allowFontChanges,
      allowHugeFontSize: this.allowHugeFontSize,
      allowAlignment: this.allowAlignment,
      allowHtml: this.allowHtml,
      allowLink: this.allowLink,
      disableHtml: !text,
      alignment: this.alignment || 'center',
      urlMode: href && !text?.length,
      href: href,
      position: this.getToolbarPosition(),
      fontFamily: this.fontFamily,
      defaultFontFamily: this.defaultFontFamily || 'AkkuratLL',
      fontSize: this.fontSize,
      fontWeight: this.fontWeight,
      textColor: this.textColor,
      allowTextColorReset: this.allowTextColorReset,
      showContext: this.showContext,
      darkBackground: this.darkBackground,
      meta: getSelectedTextNodeMeta(this.elRef.nativeElement),
      onLink: this.addLink.bind(this),
      onAction: this.onAction.bind(this),
      restoreSelection: this.restoreSelection.bind(this),
      onChangeFont: this.onChangeFont.bind(this),
      onFontSizeChanged: this.onFontSizeChanged.bind(this),
      onFontWeightChanged: this.onFontWeightChanged.bind(this),
      onAlignmentChanged: this.onAlignmentChanged.bind(this),
      onTextColorChanged: this.onTextColorChanged.bind(this),
      onContextClicked: this.contextClicked.bind(this),
      wysiwygHoverIdentifier: this.wysiwygHoverIdentifier,
      onClose: () => (this.toolbarOpen = false),
    });
    const range = getSelectedRange(this.elRef.nativeElement);
    if (range) this.storedRange = range;
  }

  private sanitize(unsafeHtml: string) {
    const html = this._sanitizer.sanitize(SecurityContext.HTML, unsafeHtml);
    if (html === '<br>') return '';
    return unicodeToChar(html);
  }

  private getHtmlValue() {
    const html = this.elRef.nativeElement.innerHTML.trim();
    if (html === '<br>') return '';
    return html;
  }

  onAction() {
    this.save();
  }

  onChangeFont(fontFamily: string) {
    this.fontFamily = fontFamily;
    this.restoreSelection();
    this.onFontChange.emit(fontFamily);
    this.onAction();
  }

  onFontSizeChanged(size: 'small' | 'medium' | 'large') {
    this.onFontSizeChange.emit(size);
    this.onAction();
  }

  onFontWeightChanged(weight: string) {
    this.onFontWeightChange.emit(weight);
    this.onAction();
  }

  async onAlignmentChanged(alignment: 'left' | 'center' | 'right') {
    this.onAlignmentChange.emit(alignment);
    this.alignment = alignment;
    this.onAction();
    await sleep(0);
    this.setToolbar();
  }

  onTextColorChanged(color: string) {
    this.onTextColorChange.emit(color);
    this.onAction();
  }

  contextClicked(color: string) {
    this.onContextClicked.emit(color);
    this.onAction();
  }

  restoreSelection() {
    if (!this.storedRange) return;
    window.getSelection().removeAllRanges();
    window.getSelection().addRange(this.storedRange);
  }

  addLink(href: string) {
    this.restoreSelection();
    const aEl = getSelectedElementOfType(this.elRef.nativeElement, 'A');
    if (!href || aEl) fullySelectSelectedElementOfType(this.elRef.nativeElement, 'A');

    if (href) {
      document.execCommand('createLink', false, href);
    } else {
      document.execCommand('unlink', false, null);
    }
  }

  addMention(user: AutoCompleteItem) {
    const fullWordRange = getCurrentWordRange(this.elRef.nativeElement);
    fullWordRange.deleteContents();

    document.execCommand(
      'insertHTML',
      false,
      `&nbsp;${getMentionHtml(user, this.avatarService.getAvatar(user))}&nbsp;`
    );
  }

  deleteMention() {
    const mentionEl = getSelectedElementOfType(this.elRef.nativeElement, 'MENTION');
    if (!mentionEl) return;
    const range = new Range();
    range.selectNode(mentionEl);
    window.getSelection().removeAllRanges();
    window.getSelection().addRange(range);
    document.execCommand('delete', false, null);
    // prevent typed text from inheriting the mention style without adding extra space
    document.execCommand('insertHTML', false, '&nbsp;');
    document.execCommand('delete', false, null);
  }

  private refreshView() {
    this.elRef.nativeElement.innerHTML = this.model;
  }

  private save() {
    let value = this.getHtmlValue();
    const text = getHtmlText(value);
    if (this.maxLength && text.length > this.maxLength) {
      value = text.slice(0, this.maxLength);
      this.toasts.add('Max length exeeded', 'error');
      this.model = value;
      this.refreshView();
    }
    if (this.lastViewModel === value) return;
    this.lastViewModel = value;
    this.wysiwygChange.emit(value);
  }

  private get contenteditable() {
    const contenteditable = this.elRef.nativeElement.getAttribute('contenteditable');
    return contenteditable === 'true' || contenteditable === '';
  }
}
